'use client';
import clsx from 'clsx';
import { useRef, useState, useEffect } from 'react';

// A client-side cache to ensure the same component type ref is returned for each
// tag, so that React doesn't unmount and remount the element (replaying the animation)
const cache = new Map();
const debugCache = new Map();

/**
 * Proxy that returns a scroll-reveal component for any HTML element.
 * The element slides (and optionally fades) in once when it enters the viewport.
 *
 * @param {boolean} [animate=true] - Apply animations
 * @param {'bottom'|'top'|'left'|'right'} [from='bottom'] - Direction the element enters from
 * @param {boolean} [fade=false] - Also animate opacity while sliding in
 * @param {number} [delayMs=0] - ms before animation starts
 * @param {number} [durationMs=1200] - ms the animation takes
 * @param {string} [distance='20rem'] - How far the element travels
 * @param {string} [rootMargin='0px 0px -15% 0px'] - IntersectionObserver root margin

 *
 * @example
 * <revealed.div className="flex flex-col gap-4" from="bottom" delayMs={120}>
 *   <h2>Hello</h2>
 * </revealed.div>
 *
 * <revealed.picture className="absolute inset-0" from="right" delayMs={260}>
 *   <source ... />
 *   <img ... />
 * </revealed.picture>
 */
export const revealed = new Proxy(
  {},
  {
    get(_, tag) {
      if (!cache.has(tag)) cache.set(tag, createRevealComponent(tag));
      return cache.get(tag);
    }
  }
);

/**
 * Drop-in debug for `revealed` that adds a colored outline to each element.
 * Elements are rendered at low opacity before triggering, so you can see them
 * in the viewport and watch the intersection happen.
 *
 * Amber outline = waiting to enter viewport, blue outline = revealed.
 *
 * @example
 * // 1. Swap the import
 * import { revealedDebug as revealed } from '@/lib/animation/revealed';
 * import { RootMarginGuide } from '@/lib/animation/RootMarginGuide';
 *
 * // 2. Drop the guide line into the page
 * <RootMarginGuide rootMargin="0px 0px -15% 0px" />
 */
export const revealedDebug = new Proxy(
  {},
  {
    get(_, tag) {
      if (!debugCache.has(tag))
        debugCache.set(tag, createRevealComponent(tag, true));
      return debugCache.get(tag);
    }
  }
);

function createRevealComponent(tag, debug = false) {
  return function RevealElement({
    children,
    className,
    from = 'bottom',
    fade = false,
    delayMs = 0,
    durationMs = 1200,
    distance = '20rem',
    rootMargin = '0px 0px -15% 0px',
    animate = true,
    ...props
  }) {
    const ref = useRef(null);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
      if (!ref.current || typeof IntersectionObserver === 'undefined') {
        setIsInView(true);
        return;
      }
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        },
        { threshold: 0, rootMargin }
      );
      observer.observe(ref.current);
      return () => observer.disconnect();
    }, [rootMargin]);

    const enterClass = fade ? 'animate-fade-enter' : 'animate-slide-enter';
    const hiddenClass = debug ? 'opacity-25' : fade ? 'opacity-0' : 'invisible';

    const translateX = { right: distance, left: `-${distance}` };
    const translateY = { bottom: distance, top: `-${distance}` };

    const Tag = tag || 'div';
    return (
      <>
        {animate ? (
          <Tag
            ref={ref}
            className={clsx(
              isInView ? enterClass : hiddenClass,
              'motion-reduce:visible motion-reduce:animate-none motion-reduce:opacity-100',
              debug && [
                'outline-2',
                isInView ? 'outline-blue-400' : 'outline-amber-400'
              ],
              className
            )}
            style={{
              animationDelay: `${delayMs}ms`,
              animationDuration: `${durationMs}ms`,
              '--translate-x-amount': translateX[from] ?? '0rem',
              '--translate-y-amount': translateY[from] ?? '0rem'
            }}
            {...props}
          >
            {children}
          </Tag>
        ) : (
          <Tag className={className} {...props}>
            {children}
          </Tag>
        )}
      </>
    );
  };
}
